import React, { useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import { MailOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { loginUser, registerUser } from "../../services/apiRequests";
import { LOGIN_CONTENT } from "../../utilities/localization";
import { useStatus } from "../../context/StatusContext";
import "./Login.css";

function Login({ visible, onClose, mode: initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { showStatus } = useStatus();

  const onFinish = async (values) => {
    try {
      setLoading(true);

      if (mode === "signin") {
        const response = await loginUser({
          email: values.email.toLowerCase(),
          password: values.password,
        });

        if (response?.data?.success) {
          showStatus({
            type: "success",
            content:
              response?.data?.message || LOGIN_CONTENT.MESSAGES.LOGIN_SUCCESS,
          });

          onClose();
          form.resetFields();
        } else {
          showStatus({
            type: "error",
            content:
              response?.data?.message || LOGIN_CONTENT.MESSAGES.LOGIN_FAILED,
          });
        }
      } else {
        const response = await registerUser({
          username: values.username,
          email: values.email.toLowerCase(),
          password: values.password,
        });

        if (response?.data?.success) {
          showStatus({
            type: "success",
            content:
              response?.data?.message || LOGIN_CONTENT.MESSAGES.SIGNUP_SUCCESS,
          });

          onClose();
          form.resetFields();
        } else {
          showStatus({
            type: "error",
            content:
              response?.data?.message || LOGIN_CONTENT.MESSAGES.SIGNUP_FAILED,
          });
        }
      }
    } catch (error) {
      showStatus({
        type: "error",
        content:
          error?.response?.data?.message || LOGIN_CONTENT.MESSAGES.AUTH_FAILED,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleModeChange = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    form.resetFields();
  };

  React.useEffect(() => {
    setMode(initialMode);
    if (visible) form.resetFields();
  }, [initialMode, visible, form]);

  return (
    <>
      <Modal
        open={visible}
        onCancel={handleClose}
        footer={null}
        centered
        width={420}
        title={null}
        className="login-modal"
      >
        <div className="login-header">
          <h2>
            {mode === "signin"
              ? LOGIN_CONTENT.SIGNIN.TITLE
              : LOGIN_CONTENT.SIGNUP.TITLE}
          </h2>

          <p>
            {mode === "signin"
              ? LOGIN_CONTENT.SIGNIN.SUBTITLE
              : LOGIN_CONTENT.SIGNUP.SUBTITLE}
          </p>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {mode === "signup" && (
            <Form.Item
              name="username"
              label={LOGIN_CONTENT.FIELDS.USERNAME}
              rules={[
                {
                  required: true,
                  message: LOGIN_CONTENT.VALIDATION.USERNAME_REQUIRED,
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={LOGIN_CONTENT.PLACEHOLDERS.USERNAME}
              />
            </Form.Item>
          )}

          <Form.Item
            name="email"
            label={LOGIN_CONTENT.FIELDS.EMAIL}
            rules={[
              {
                required: true,
                message: LOGIN_CONTENT.VALIDATION.EMAIL_REQUIRED,
              },
              {
                type: "email",
                message: LOGIN_CONTENT.VALIDATION.EMAIL_INVALID,
              },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={LOGIN_CONTENT.PLACEHOLDERS.EMAIL}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={LOGIN_CONTENT.FIELDS.PASSWORD}
            rules={[
              {
                required: true,
                message: LOGIN_CONTENT.VALIDATION.PASSWORD_REQUIRED,
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={LOGIN_CONTENT.PLACEHOLDERS.PASSWORD}
            />
          </Form.Item>

          {mode === "signup" && (
            <Form.Item
              name="confirm"
              label={LOGIN_CONTENT.FIELDS.CONFIRM_PASSWORD}
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: LOGIN_CONTENT.VALIDATION.CONFIRM_REQUIRED,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error(LOGIN_CONTENT.VALIDATION.PASSWORDS_MISMATCH)
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={LOGIN_CONTENT.PLACEHOLDERS.CONFIRM_PASSWORD}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              className="login-submit-btn"
            >
              {mode === "signin"
                ? LOGIN_CONTENT.SIGNIN.BUTTON
                : LOGIN_CONTENT.SIGNUP.BUTTON}
            </Button>
          </Form.Item>
        </Form>

        <div className="login-toggle">
          <span>
            {mode === "signin"
              ? LOGIN_CONTENT.TOGGLE_SIGNIN_Q
              : LOGIN_CONTENT.TOGGLE_SIGNUP_Q}
          </span>

          <Button type="link" onClick={handleModeChange}>
            {mode === "signin"
              ? LOGIN_CONTENT.TOGGLE_SIGNUP
              : LOGIN_CONTENT.TOGGLE_SIGNIN}
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default Login;